import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  BookOpenIcon,
  ChatBubbleLeftRightIcon,
  CheckIcon,
  CircleStackIcon,
  CodeBracketIcon,
  CpuChipIcon,
  GlobeAltIcon,
  PaintBrushIcon,
  PauseIcon,
  PlayIcon,
  RocketLaunchIcon,
} from '@heroicons/react/24/outline'
import { Container } from '@/components/ui'

const productStages = [
  {
    title: 'Yêu cầu khách hàng',
    description: 'Ý tưởng · Mục tiêu · Ngữ cảnh nghiệp vụ',
    status: 'Đầu vào',
    icon: ChatBubbleLeftRightIcon,
    done: false,
  },
  {
    title: 'Phân tích yêu cầu',
    description: 'Nghiệp vụ · Chức năng · Phi chức năng',
    status: 'Hoàn tất',
    icon: CheckIcon,
    done: true,
  },
  {
    title: 'User Story & Use Case',
    description: 'User Story · Use Case · Business Rules',
    status: 'Hoàn tất',
    icon: BookOpenIcon,
    done: true,
  },
  {
    title: 'Thiết kế CSDL',
    description: 'ERD · Từ điển dữ liệu · Quan hệ',
    status: 'Hoàn tất',
    icon: CircleStackIcon,
    done: true,
  },
  {
    title: 'Đặc tả API',
    description: 'REST API · JSON · Swagger',
    status: 'Hoàn tất',
    icon: CpuChipIcon,
    done: true,
  },
  {
    title: 'Prototype tương tác',
    description: 'HTML · User Flow · Review',
    status: 'Hoàn tất',
    icon: PaintBrushIcon,
    done: true,
  },
  {
    title: 'AI Coding',
    description: 'Sinh tính năng · Hỗ trợ AI · Refactor',
    status: 'Hoàn tất',
    icon: CodeBracketIcon,
    done: true,
  },
  {
    title: 'Deploy ứng dụng',
    description: 'Cloud · Domain · SSL',
    status: 'Hoàn tất',
    icon: RocketLaunchIcon,
    done: true,
  },
  {
    title: 'Sản phẩm live',
    description: 'URL công khai · Portfolio · Demo',
    status: 'Đang chạy',
    icon: GlobeAltIcon,
    done: false,
    final: true,
  },
]

const outcomeUses = ['Portfolio', 'Phỏng vấn', 'Dùng cá nhân']
const MOBILE_AUTOPLAY_MS = 1000
const MOBILE_RESUME_MS = 800

const EaseHero = () => {
  const productGridRef = useRef<HTMLDivElement>(null)
  const [autoplayPaused, setAutoplayPaused] = useState(false)
  const [activeStage, setActiveStage] = useState(0)

  useEffect(() => {
    const grid = productGridRef.current
    const mobileQuery = window.matchMedia('(max-width: 767px)')
    const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)')

    if (!grid || !mobileQuery.matches || reducedMotionQuery.matches || autoplayPaused) {
      return
    }

    let currentStage = 0
    let resumeTimer: number | undefined
    let autoplayTimer: number | undefined

    const moveToNextStage = () => {
      currentStage = (currentStage + 1) % productStages.length
      setActiveStage(currentStage)
      const cards = grid.querySelectorAll<HTMLElement>('.eh-product-card')
      const nextCard = cards.item(currentStage)

      grid.scrollTo({
        left: nextCard?.offsetLeft ?? 0,
        behavior: 'smooth',
      })
    }

    autoplayTimer = window.setInterval(moveToNextStage, MOBILE_AUTOPLAY_MS)

    const pauseAutoplay = () => {
      if (autoplayTimer) window.clearInterval(autoplayTimer)
      if (resumeTimer) window.clearTimeout(resumeTimer)
    }

    const resumeAutoplay = () => {
      pauseAutoplay()
      resumeTimer = window.setTimeout(() => {
        const cardWidth = grid.querySelector<HTMLElement>('.eh-product-card')?.offsetWidth ?? 1
        currentStage = Math.round(grid.scrollLeft / Math.max(cardWidth, 1))
        setActiveStage(currentStage)
        autoplayTimer = window.setInterval(moveToNextStage, MOBILE_AUTOPLAY_MS)
      }, MOBILE_RESUME_MS)
    }

    grid.addEventListener('touchstart', pauseAutoplay, { passive: true })
    grid.addEventListener('touchend', resumeAutoplay, { passive: true })

    return () => {
      pauseAutoplay()
      grid.removeEventListener('touchstart', pauseAutoplay)
      grid.removeEventListener('touchend', resumeAutoplay)
    }
  }, [autoplayPaused])

  return (
    <section className="pb-10 pt-3 md:pb-section md:pt-4" aria-labelledby="home-hero-title">
      <Container>
        <div className="grid gap-4 md:min-h-[560px] md:grid-cols-[0.95fr_1.25fr] md:gap-3.5">
          <div className="eh-reveal eh-panel flex flex-col justify-between px-5 py-7 sm:px-7 sm:py-9 md:min-h-[520px] md:px-[42px] md:py-[49px]">
            <div className="max-w-[520px]">
              <p className="eh-eyebrow">M2MBA Academy · Career Path</p>
              <h1
                id="home-hero-title"
                className="eh-display text-[clamp(2rem,6.5vw,4.5rem)] tracking-[-0.035em]"
              >
                Trở thành Business Analyst thực chiến trong thời đại{' '}
                <em className="not-italic font-normal">AI</em>
              </h1>
              <p className="mt-5 max-w-[460px] text-base leading-relaxed text-eh-muted md:mt-7 md:text-[17px]">
                Làm chủ Business Analysis, AI và kỹ năng hệ thống qua lộ trình thực chiến, để bạn đi
                từ nền tảng BA đến khả năng tự tạo sản phẩm cùng công nghệ AI.
              </p>
            </div>
            <div className="mt-8 flex flex-wrap gap-3 md:mt-10">
              <a href="#stage" className="eh-btn">
                Khám phá lộ trình <span className="eh-arrow">→</span>
              </a>
              <Link to="/courses" className="eh-btn-secondary">
                Xem khóa học
              </Link>
            </div>
          </div>

          <div className="eh-product-journey eh-reveal" aria-label="Quy trình xây dựng sản phẩm thật">
            <div className="relative z-[2] flex items-end justify-between gap-5">
              <div>
                <p className="mb-1 text-eh-caption font-bold uppercase tracking-[0.14em] text-eh-accent-soft">
                  Từ ý tưởng đến ứng dụng chạy thật
                </p>
                <h2 className="font-display text-[clamp(1.5rem,2.5vw,2.375rem)] font-medium leading-none tracking-[-0.035em] text-white">
                  Xây sản phẩm thật
                </h2>
              </div>
              <p className="hidden max-w-[220px] text-right text-eh-caption leading-relaxed text-white/55 sm:block">
                Mỗi bước tạo ra một đầu ra thật để đưa vào portfolio của bạn.
              </p>
            </div>

            <div className="relative">
              <div
                ref={productGridRef}
                className="eh-product-grid"
                aria-live="polite"
                aria-atomic="false"
              >
                <svg
                  className="eh-product-flow"
                  viewBox="0 0 900 430"
                  preserveAspectRatio="none"
                  aria-hidden="true"
                >
                  <path
                    id="ease-product-flow"
                    d="M130 68 H450 H770 Q805 68 805 104 V215 Q805 232 770 232 H450 H130 Q95 232 95 268 V362 Q95 380 130 380 H450 H770"
                  />
                  {[0, -2.35, -4.7].map((begin, index) => (
                    <circle key={begin} r={5 - index} opacity={1 - index * 0.24}>
                      <animateMotion dur="7s" begin={`${begin}s`} repeatCount="indefinite">
                        <mpath href="#ease-product-flow" />
                      </animateMotion>
                    </circle>
                  ))}
                </svg>

                {productStages.map((stage, index) => {
                  const Icon = stage.icon
                  return (
                    <article
                      key={stage.title}
                      className={`eh-product-card${stage.final ? ' is-final' : ''}`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="eh-product-icon">
                          <Icon className="h-[17px] w-[17px]" aria-hidden="true" />
                        </span>
                        <span className="eh-product-status">
                          {stage.done ? (
                            <CheckIcon className="h-2.5 w-2.5" aria-hidden="true" />
                          ) : null}
                          {stage.status}
                        </span>
                      </div>
                      <p className="mt-2.5 text-[13px] font-semibold leading-tight text-white">
                        <span className="mr-1.5 text-[10px] font-medium text-eh-accent-mid">
                          {String(index + 1).padStart(2, '0')}
                        </span>
                        {stage.title}
                      </p>
                      <p className="mt-1 truncate text-[11px] text-white/50">{stage.description}</p>
                    </article>
                  )
                })}
              </div>

              <div className="mt-3 flex items-center justify-between gap-3 md:hidden">
                <p className="text-eh-caption text-white/55">
                  Bước {activeStage + 1}/{productStages.length}
                </p>
                <button
                  type="button"
                  className="inline-flex min-h-11 items-center gap-2 rounded-eh-sm border border-white/20 bg-white/10 px-3 py-2 text-eh-caption font-semibold text-white"
                  onClick={() => setAutoplayPaused((prev) => !prev)}
                  aria-pressed={autoplayPaused}
                >
                  {autoplayPaused ? (
                    <>
                      <PlayIcon className="h-4 w-4" aria-hidden />
                      Phát
                    </>
                  ) : (
                    <>
                      <PauseIcon className="h-4 w-4" aria-hidden />
                      Tạm dừng
                    </>
                  )}
                </button>
              </div>
            </div>

            <div
              className="eh-product-outcome hidden md:block"
              aria-label="Kết quả cuối cùng của học viên"
            >
              <div className="eh-product-browser">
                <div className="eh-product-browser__bar">
                  <span className="eh-product-browser__dots" aria-hidden="true">
                    <i /><i /><i />
                  </span>
                  <span className="eh-product-browser__url">
                    my-product.m2mba.vn
                  </span>
                  <span className="eh-product-browser__live">
                    <span className="eh-product-browser__pulse" aria-hidden="true" />
                    Đang chạy
                  </span>
                </div>
                <div className="eh-product-browser__body">
                  <div className="eh-product-browser__preview" aria-hidden="true">
                    <div className="eh-product-browser__appbar">
                      <strong>TaskFlow</strong>
                      <span>Dashboard</span>
                    </div>
                    <div className="eh-product-browser__hero-block">
                      <p>Xin chào, BA</p>
                      <span>3 feature sẵn sàng demo</span>
                    </div>
                    <div className="eh-product-browser__feature-row">
                      <span>Yêu cầu</span>
                      <span>Prototype</span>
                      <span>Deploy</span>
                    </div>
                  </div>
                  <div className="eh-product-browser__copy">
                    <p className="eh-product-browser__label">Kết quả cuối cùng</p>
                    <p className="eh-product-browser__title">
                      Một ứng dụng đang chạy trên web
                    </p>
                    <p className="eh-product-browser__desc">
                      Sẵn sàng đưa vào portfolio, demo phỏng vấn, hoặc dùng cho nhu cầu cá nhân.
                    </p>
                    <div className="eh-product-browser__chips">
                      {outcomeUses.map((use) => (
                        <span key={use}>{use}</span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Container>
    </section>
  )
}

export default EaseHero
